/**
 * Card Focus Visible helper class.
 * 
 * Focus visible (keyboard focus) functionality.
 * 
 * Adds `FOCUS_VISIBLE_CLASS` class to card when the card itself or one of card's child elements get focus by keyboard.
 * 
 * Focus-visible (which has outline style) is supposed to work only by keyboard interaction, so we can't use 
 *   regular `focus` or `blur` to add class.
 * 
 * Only use this on cards with streched-link that needs -focus-visible class.
 *
 * @module components/card-focus-visible
 */
import Component from './component';
export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';

  // class added to the card when the card itself or one of card's child elements get focus by keyboard.
  const FOCUS_VISIBLE_CLASS = '-focus-visible';

  /**
   * Card Focus Visible component class
   */
  class CardFocusVisible extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, options);
      this.name = 'card-focus-visible';
      this.options = $.extend(true, {}, CardFocusVisible.default, options);
      this.init();
    }

    /**
     * Main initializer
     * 
     * @returns void
     */
    init() {
      const el = this.element;

      this._cleanup();
      
      el.data('card-focus-visible-instance', this);

      this.__onDocKeydown = this._onDocKeydown.bind(this);
      this.__onDocFocus = this._onDocFocus.bind(this);

      el.on('focusout.cardFocusVisible', this.options.cardSelector, (ev) => {
        if (this._elFocusVisible) {
          this._elFocusVisible.removeClass(FOCUS_VISIBLE_CLASS);
          this._elFocusVisible = null;
        }
      });

      document.addEventListener('keydown', this.__onDocKeydown, true);
      document.addEventListener('focus', this.__onDocFocus, true);
    }

    /**
     * Clean up existing instance
     */
    _cleanup() {
      const inst = this.element.data('card-focus-visible-instance');

      if (inst) {
        this.element.off('.cardFocusVisible');
        if (inst.__onDocKeydown) {
          document.removeEventListener('keydown', inst.__onDocKeydown);
        }
        if (inst.__onDocFocus) {
          document.removeEventListener('focus', inst.__onDocFocus);
        }
      }

      this.element.removeData('card-focus-visible-instance');

      this._elFocusVisible = null;
      this._hadFocusByKeyboard = false;
      this._checkActiveElementTimeout = null;
    }

    /**
     * Document focus event handler.
     * 
     * Blur (focusout) was fired when the tab/window become inactive (switch tab/window, minimized, etc.), , but browser will
     *   restore the focus-visible back after the tab/window become active again.
     * Since we are adding `FOCUS_VISIBLE_CLASS` class to the Card (the the actual element that get the :focus-visible), we
     *   have to detect it.
     * We kept that state in `this._hadFocusByKeyboard`, and restore it back manually after the document get focus.
     * 
     * @param {Event} ev 
     */
    _onDocFocus(ev) {
      if (this._hadFocusByKeyboard) {
        this._checkActiveElement();
      }
    }

    /**
     * Document keydown event handler
     * 
     * @param {Event} ev 
     * @returns 
     */
    _onDocKeydown(ev) {
      if (ev.metaKey || ev.altKey || ev.ctrlKey) {
        return;
      }

      this._checkActiveElement();
    }

    _checkActiveElement() {
      window.clearTimeout(this._checkActiveElementTimeout);
      this._checkActiveElementTimeout = window.setTimeout(() => {
        if (this._elFocusVisible) {
          this._elFocusVisible.removeClass(FOCUS_VISIBLE_CLASS);
        }

        this._elFocusVisible  = null;
        this._hadFocusByKeyboard = false;

        if ((document.activeElement === this.element[0]) || $.contains(this.element[0], document.activeElement)) {
          this._elFocusVisible = $(document.activeElement).closest(this.options.cardSelector).addClass(FOCUS_VISIBLE_CLASS);
          this._hadFocusByKeyboard = true;
        }
      });
    }
  }

  CardFocusVisible.default = {
    cardSelector: '.card'
  };
  
  return CardFocusVisible;
})(jQuery, window);